import { List, ListItem, Stack, Typography } from '@mui/material';
import { type ExposureOrGroup, useSubmission } from 'api/submission';
import {
  APPLICATION_STEPS,
  defaultBackendDateformat,
  defaultDateformat,
  firstDayOfYear1900,
  VALIDATION_MESSAGES,
} from 'common/constants';
import { displayBackendErrorMessage } from 'components/DisplayBackEndErrorMessage';
import { DateField } from 'components/lib/DateField';
import { FormCheckbox } from 'components/lib/FormCheckbox';
import { PrimaryButton } from 'components/lib/PrimaryButton';
import type { CheckboxValues, Field } from 'components/lib/QuestionEngine';
import { TextField } from 'components/lib/TextField';
import { Modal } from 'components/Modal';
import { useFormik } from 'formik';
import { formatDate, parseLocation } from 'helpers/utils';
import { useExposuresOrGroupFields } from 'hooks/useExposuresOrGroupFields';
import { isEmpty, omit } from 'lodash-es';
import {
  type ChangeEvent,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { IOwner } from 'routes/Exposures/Owners/Owners.types';
import * as yup from 'yup';

import { ManualAddressInput } from 'components/ManualAddressInput';
import type { ManualAddressInputRef } from 'components/ManualAddressInput/ManualAddressInput.types';
import { DateTime } from 'luxon';
import { useParams } from 'react-router-dom';
import type { OwnerModalProps, OwnerModalRef } from './OwnerModal.types';

type TGroup = ExposureOrGroup<IOwner>;

const { fields: initialFields } = APPLICATION_STEPS.owners;

const handleCheckYesOrNo = (value: boolean): CheckboxValues => (value ? 'Yes' : 'No');

const OwnerModal = forwardRef<OwnerModalRef | undefined, OwnerModalProps>(
  // eslint-disable-next-line complexity
  ({ handleAdd, handleUpdate, category }, ref) => {
    const [open, setOpen] = useState(false);

    const [editingGroup, setEditingGroup] = useState<TGroup>({} as TGroup);

    const { id: submissionId } = useParams() as { id: string };
    const { data: submission } = useSubmission(submissionId);

    const manualAddressInputRef = useRef<ManualAddressInputRef>(undefined);

    const { relatedResource: owners } = useExposuresOrGroupFields<IOwner>({
      type: 'group',
      name: 'pol_oo',
    });

    const fields = useMemo(
      () =>
        initialFields.reduce(
          (acc, field) => {
            if (field.code === 'pol_oo_isprimary') {
              const tempField = {
                ...field,
                name: `Primary ${category}`,
              };
              acc[field.code] = tempField;
            } else {
              acc[field.code] = field;
            }

            return acc;
          },
          {} as {
            [key in keyof IOwner]: Field;
          },
        ),
      [initialFields, category],
    );

    const isOwnersEmpty = owners.length === 0;

    // OH state should always have pol_wc_included=="No" regardless of answers to the questions (pol_wc_included - policy level field)
    // Which means that the pol_oo_wc_excluded should be set to "Yes" by default (pol_oo_wc_excluded - owner level field)
    // https://linear.app/three-insurance/issue/ENG-4685/consumer-submission-for-oh#comment-be9a0122
    const isStateOH = submission?.us_state === 'OH';

    const initialStateValue: IOwner = {
      pol_oo_name_first: '',
      pol_oo_name_last: '',
      pol_oo_home_address: '',
      pol_oo_isprimary: isOwnersEmpty ? 'Yes' : 'No',
      pol_oo_ncf_optin: 'No',
      pol_oo_wc_excluded: isStateOH ? 'Yes' : 'No',
    };

    const [state, setState] = useState<IOwner>(initialStateValue as IOwner);

    const isEditing = !isEmpty(editingGroup);

    const title = `${isEditing ? 'Update' : 'Add'} ${category}`;

    const isPrimary = state.pol_oo_isprimary !== 'No';

    const formik = useFormik<IOwner>({
      initialValues: {
        ...state,
      },
      validationSchema: yup.lazy(() =>
        yup.object({
          pol_oo_name_first: yup.string().required(),
          pol_oo_name_last: yup.string().required(),
          pol_oo_isprimary: yup.string(),
          pol_oo_ncf_optin: yup.string(),
          pol_oo_dob: yup
            .date()
            .typeError(VALIDATION_MESSAGES.invalidDateFormat)
            .min(firstDayOfYear1900, `The date must be after 1900`)
            .max(DateTime.now().startOf('day').toJSDate(), `Date must be earlier than today`)
            .when('pol_oo_isprimary', {
              is: (pol_oo_isprimary: CheckboxValues) => pol_oo_isprimary === 'Yes',
              then: (schema) => schema.required(),
              otherwise: (schema) => schema.notRequired(),
            }),
          pol_oo_home_address: yup.string().when('pol_oo_isprimary', {
            is: (pol_oo_isprimary: CheckboxValues) => pol_oo_isprimary === 'Yes',
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.notRequired(),
          }),
        }),
      ),
      onSubmit: (_values) => {},
    });

    const validateFormik = async () => {
      await formik.submitForm();
      const errors = await formik.validateForm();

      if (!isEmpty(errors)) {
        await formik.setTouched(
          {
            ...formik.touched,
            ...Object.keys(fields).reduce((acc, key) => {
              acc[key] = true;
              return acc;
            }, {}),
          },
          false,
        );

        return false;
      }

      return true;
    };

    useImperativeHandle(ref, () => ({
      openModal: (group) => {
        if (group) {
          setEditingGroup(group);
          setState?.(group.data);
          formik?.setValues(group.data);
        } else {
          setState(initialStateValue);
          formik?.resetForm({
            values: initialStateValue,
          });
        }

        setOpen(true);
      },
    }));

    const resetModal = () => {
      setOpen(false);
      formik?.resetForm({ values: initialStateValue });
      setState?.(initialStateValue);
      setEditingGroup({} as TGroup);
    };

    const handleSubmit = async () => {
      const isManualAddressInputValid = await manualAddressInputRef.current?.validate();

      const isValid = await validateFormik();

      if (!isValid) return;

      if (isPrimary) {
        if (!isManualAddressInputValid) {
          return;
        }

        try {
          const missingAddressParts: string[] = [];

          const parsedAddress = parseLocation(formik.values.pol_oo_home_address);

          Object.entries(omit(parsedAddress, 'secondary')).forEach(([key, value]) => {
            if (isEmpty(value)) {
              missingAddressParts.push(key);
            }
          });

          const isAddressValid = missingAddressParts.length === 0;

          if (!isAddressValid) {
            throw Error(`Address is invalid, missing parts: ${missingAddressParts.join(', ')}`);
          }
        } catch (e) {
          displayBackendErrorMessage(e);
          return;
        }
      }

      const values: IOwner = {
        ...formik.values,
        pol_oo_dob: formik.values.pol_oo_dob
          ? formatDate(formik.values.pol_oo_dob, defaultBackendDateformat)
          : '',
      };

      if (isEditing) {
        await handleUpdate(values, editingGroup?.id);
      } else {
        await handleAdd(values);
      }

      resetModal();
    };

    const updateState = (name: string, value: unknown) => {
      setState?.((prevState) => ({ ...prevState, [name]: value }));

      formik?.setFieldValue(name, value);
    };

    const handlePickChange = (value: Date | null) => {
      updateState(fields.pol_oo_dob?.code!, value);
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = event.target;

      updateState(name, value);
    };

    return (
      <Modal
        open={open}
        title={title}
        onClose={resetModal}
        onCloseIconClick={() => resetModal()}
        modalContent={
          <Stack gap={4}>
            <Stack gap={1.5}>
              <Typography variant="h2">Tell us about the {category}</Typography>

              <TextField
                id={fields.pol_oo_name_first.code}
                name={fields.pol_oo_name_first.code}
                label={fields.pol_oo_name_first.name}
                field={fields.pol_oo_name_first}
                testPrefix={fields.pol_oo_name_first.code}
                onChange={handleInputChange}
                value={state.pol_oo_name_first || ''}
                error={
                  formik.touched[`${fields.pol_oo_name_first.code}`] &&
                  Boolean(formik.errors[`${fields.pol_oo_name_first.code}`])
                }
                helperText={
                  formik.touched[`${fields.pol_oo_name_first.code}`] &&
                  (formik.errors[`${fields.pol_oo_name_first.code}`] as string)
                }
              />

              <TextField
                id={fields.pol_oo_name_last.code}
                name={fields.pol_oo_name_last.code}
                label={fields.pol_oo_name_last.name}
                field={fields.pol_oo_name_last}
                testPrefix={fields.pol_oo_name_last.code}
                onChange={handleInputChange}
                value={state.pol_oo_name_last || ''}
                error={
                  formik.touched[`${fields.pol_oo_name_last.code}`] &&
                  Boolean(formik.errors[`${fields.pol_oo_name_last.code}`])
                }
                helperText={
                  formik.touched[`${fields.pol_oo_name_last.code}`] &&
                  (formik.errors[`${fields.pol_oo_name_last.code}`] as string)
                }
              />
            </Stack>

            <FormCheckbox
              labelText={fields.pol_oo_isprimary.name || ''}
              isFieldOptional={!isOwnersEmpty}
              checked={isPrimary}
              disabled={isOwnersEmpty || editingGroup?.data?.pol_oo_isprimary === 'Yes'}
              checkboxProps={{
                onChange: (e) =>
                  updateState(
                    fields.pol_oo_isprimary.code,
                    handleCheckYesOrNo(e.currentTarget.checked),
                  ),
              }}
            />

            <DateField
              disabled={!isPrimary}
              value={state.pol_oo_dob}
              onChange={handlePickChange}
              maxDate={DateTime.now().startOf('day').toJSDate()}
              label={fields.pol_oo_dob?.name}
              format={defaultDateformat}
              slotProps={{
                textField: {
                  error:
                    formik.touched[fields.pol_oo_dob?.code!] &&
                    Boolean(formik.errors[fields.pol_oo_dob?.code!]),
                  helperText:
                    formik.touched[fields.pol_oo_dob?.code!] &&
                    (formik.errors[fields.pol_oo_dob?.code!] as string),
                  inputProps: {
                    'data-test': 'pol_birth_date',
                  },
                },
              }}
            />

            <Stack>
              <Typography variant="h2" mb={1.5}>
                {fields.pol_oo_home_address?.question_text}
              </Typography>

              <ManualAddressInput
                useAutoCompleteAddressInput
                isReadOnly={!isPrimary}
                ref={manualAddressInputRef}
                updateAddressField={(address: string) =>
                  updateState(String(fields?.pol_oo_home_address?.code), address)
                }
                preferStates={[submission?.us_state as string]}
                initialAddress={state.pol_oo_home_address || ''}
                useRegularParsingLogicForInitialAddress
              />
            </Stack>

            <Stack>
              <FormCheckbox
                labelText={fields.pol_oo_ncf_optin.name || ''}
                checked={state.pol_oo_ncf_optin === 'Yes'}
                disabled={!isPrimary}
                isFieldOptional={fields.pol_oo_ncf_optin.is_optional}
                checkboxProps={{
                  readOnly: !isPrimary,
                  onChange: (e) =>
                    updateState(
                      fields.pol_oo_ncf_optin.code,
                      handleCheckYesOrNo(e.currentTarget.checked),
                    ),
                }}
              />

              <List sx={{ padding: 0, listStyleType: 'disc', pl: '3rem' }}>
                {(fields.pol_oo_ncf_optin?.additional_data?.descriptionList as string[])?.map(
                  (descText) => (
                    <ListItem key={descText} sx={{ padding: 0, display: 'list-item' }}>
                      <Typography>{descText}</Typography>
                    </ListItem>
                  ),
                )}
              </List>
            </Stack>
          </Stack>
        }
        modalFooter={
          <PrimaryButton text={title} fullWidth onClick={handleSubmit} data-test="add-owner" />
        }
      />
    );
  },
);

export default OwnerModal;
